import { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Pencil, LogOut, Check, X } from 'lucide-react';
import { useI18n } from '@/context/I18nContext';
import { useRouter } from '@/context/RouterContext';
import { useAuth } from '@/context/AuthContext';
import { teamsApi } from '@/api/teams';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorState } from '@/components/common/ErrorState';
import type { Team, TeamMember } from '@/types/team';
import { TeamMembersTab } from './TeamMembersTab';
import { TeamSharingTab } from './TeamSharingTab';

export function TeamDetailPage({ teamId }: { teamId: string }) {
  const { t } = useI18n();
  const { params, navigate } = useRouter();
  const { user } = useAuth();
  const [team, setTeam] = useState<Team | null>(null);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');

  const activeTab = (params.tab as 'members' | 'sharing') || 'members';

  const isOwner = team?.ownerId === user?.id;
  const canManage = isOwner || members.some(m => m.userId === user?.id && m.role === 'manage');

  const loadTeam = useCallback(() => {
    setLoading(true);
    teamsApi.getTeam(teamId)
      .then(res => {
        const { members: memberList, ...teamData } = res as any;
        setTeam(teamData as Team);
        setMembers(memberList || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [teamId]);

  useEffect(() => { loadTeam(); }, [loadTeam]);

  const handleRename = async () => {
    if (!editName.trim() || !team) return;
    try {
      await teamsApi.updateTeam(teamId, editName.trim());
      setTeam({ ...team, name: editName.trim() });
      setEditing(false);
    } catch (e) {
      console.error(e);
    }
  };

  const handleLeave = async () => {
    if (!confirm(t('team.confirmLeave'))) return;
    try {
      await teamsApi.leaveTeam(teamId);
      navigate('#/teams');
    } catch (e) {
      console.error(e);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!team) {
    return <ErrorState message="Team not found." isNotFound />;
  }

  return (
    <div>
      <button
        onClick={() => navigate('#/teams')}
        className="flex items-center gap-1.5 text-xs text-text-muted hover:text-text-primary transition-colors mb-4 cursor-pointer"
      >
        <ArrowLeft size={14} />
        {t('common.back')}
      </button>

      <div className="flex items-center gap-3 mb-6">
        {editing ? (
          <div className="flex items-center gap-2 flex-1">
            <input
              value={editName}
              onChange={e => setEditName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleRename()}
              className="input-field px-3 py-1.5 text-lg font-semibold focus:border-border-hover"
              autoFocus
            />
            <button onClick={handleRename} className="p-1.5 text-green-400 hover:text-green-300 cursor-pointer"><Check size={18} /></button>
            <button onClick={() => setEditing(false)} className="p-1.5 text-text-muted hover:text-text-primary cursor-pointer"><X size={18} /></button>
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <h1 className="text-xl font-semibold tracking-tight truncate">{team.name}</h1>
            {isOwner && (
              <button
                onClick={() => { setEditName(team.name); setEditing(true); }}
                className="p-1.5 text-text-muted hover:text-text-primary transition-colors cursor-pointer shrink-0"
              >
                <Pencil size={14} />
              </button>
            )}
          </div>
        )}

        {!isOwner && (
          <button
            onClick={handleLeave}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-text-secondary border border-border hover:border-red-500/50 hover:text-red-400 transition-colors cursor-pointer"
          >
            <LogOut size={14} />
            {t('team.leave')}
          </button>
        )}
      </div>

      <div
        className="flex gap-1 pb-0 overflow-x-auto border-b border-border-default mb-6"
        role="tablist"
        aria-label="Team subtabs"
        style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
      >
        <button
          role="tab"
          aria-selected={activeTab === 'members'}
          onClick={() => navigate(`#/teams/${teamId}/members`)}
          className={`
            bg-none border-none text-xs px-3.5 py-2 cursor-pointer shrink-0 whitespace-nowrap
            border-b-2 -mb-px rounded-t-sm
            transition-colors duration-150
            ${activeTab === 'members'
              ? 'text-accent border-accent font-semibold'
              : 'text-text-muted border-transparent hover:text-text-primary hover:bg-bg-hover'
            }
          `}
        >
          {t('team.tab.members')}
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'sharing'}
          onClick={() => navigate(`#/teams/${teamId}/sharing`)}
          className={`
            bg-none border-none text-xs px-3.5 py-2 cursor-pointer shrink-0 whitespace-nowrap
            border-b-2 -mb-px rounded-t-sm
            transition-colors duration-150
            ${activeTab === 'sharing'
              ? 'text-accent border-accent font-semibold'
              : 'text-text-muted border-transparent hover:text-text-primary hover:bg-bg-hover'
            }
          `}
        >
          {t('team.tab.sharing')}
        </button>
      </div>

      {activeTab === 'members' && (
        <TeamMembersTab
          team={team}
          isOwner={isOwner}
          canManage={canManage}
          onUpdate={loadTeam}
        />
      )}
      {activeTab === 'sharing' && (
        <TeamSharingTab
          teamId={teamId}
          canManage={canManage}
        />
      )}
    </div>
  );
}
